import React, { useEffect, useRef } from 'react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { Extension, textblockTypeInputRule } from '@tiptap/core';
import { PluginKey, type EditorState } from '@tiptap/pm/state';
import { StarterKit } from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Placeholder } from '@tiptap/extensions';
import { Highlight } from '@tiptap/extension-highlight';
import { Superscript } from '@tiptap/extension-superscript';
import { Subscript } from '@tiptap/extension-subscript';
import { Markdown } from '@tiptap/markdown';
import type { Editor } from '@tiptap/react';
import { transformExternalPastedHtml } from '@cotera/client/app/components/app/rte/lib/normalize-pasted-html';
import type { DG } from '@cotera/sdk/core';
import { cn } from '@cotera/client/v0/lib/utils';
import { useEnterSendsMessage } from './use-enter-sends';
import {
  CoteraEmoji,
  CoteraMention,
  EMOJI_DROPDOWN_PLUGIN_KEY,
  EmojiSuggestionMenu,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { MentionResourcePicker } from '@cotera/client/app/components/resource-picker';
import { useOpenToolsModal } from '@cotera/client/v0/components/tool-connections';
import { useOptionalModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import {
  CHAT_ACTION_SLASH_PLUGIN_KEY,
  ChatActionSlashDropdownMenu,
} from './chat-action-slash-dropdown-menu';
import { UiState } from '@cotera/client/app/components/ui/tiptap/tiptap-extension/ui-state-extension';
import { HorizontalRule } from '@cotera/client/app/components/ui/tiptap/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import type {
  MentionGroup,
  MentionOption,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import type { MentionClickData } from './readonly-editor-state';

/** Plugin key used by MentionDropdownMenu's SuggestionMenu - must match mention-dropdown-menu.tsx */
const MENTION_DROPDOWN_PLUGIN_KEY = new PluginKey('mentionDropdownMenu');
/** Plugin key used by chat slash actions SuggestionMenu */
const ACTION_SLASH_DROPDOWN_PLUGIN_KEY = new PluginKey(
  CHAT_ACTION_SLASH_PLUGIN_KEY
);

/**
 * Open a code block as soon as the third backtick is typed. StarterKit's default
 * fence rule needs a trailing space/newline, but in the chat Enter sends and a
 * trailing space is unintuitive, so users expect ``` to convert on its own.
 */
const InstantCodeBlock = Extension.create({
  name: 'instantCodeBlock',
  addInputRules() {
    const codeBlock = this.editor.schema.nodes['codeBlock'];
    if (codeBlock === undefined) {
      return [];
    }
    return [textblockTypeInputRule({ find: /^```$/, type: codeBlock })];
  },
});

/** True while any @-mention / slash / emoji suggestion menu is open. */
const menusActive = (state: EditorState): boolean =>
  MENTION_DROPDOWN_PLUGIN_KEY.getState(state)?.active === true ||
  ACTION_SLASH_DROPDOWN_PLUGIN_KEY.getState(state)?.active === true ||
  EMOJI_DROPDOWN_PLUGIN_KEY.getState(state)?.active === true;

export type ChatTipTapInputProps = {
  value?: DG.DocumentEditor.DocumentState;
  onChange?: (editor: Editor) => void;
  onSetValue?: (text: string) => void;
  placeholder?: string;
  className?: string;
  /** Called when editor content changes - used for hasInput check */
  onTextChange?: (text: string) => void;
  /** When provided, enables @ mention autocomplete (e.g. for tools) */
  mentionOptions?: MentionOption<unknown>[];
  mentionGroups?: MentionGroup[];
  /** When true, shows loading indicator in mention popover (e.g. while tools load) */
  mentionLoading?: boolean;
  /** When provided, mention chips are clickable and invoke this handler */
  onMentionClick?: (data: MentionClickData) => void;
};

/**
 * Minimal TipTap editor for chat input.
 * Outputs DocumentState (type: 'doc', content) compatible with Conversation.createRichTextUserMessage.
 * Styled to match the existing chat textarea.
 */
export const ChatTipTapInput = React.forwardRef<
  {
    setValue: (text: string) => void;
    getEditor: () => Editor | null;
    setPlaceholder: (placeholder: string) => void;
  },
  ChatTipTapInputProps
>(function ChatTipTapInput(
  {
    value = { type: 'doc', content: [] },
    onChange,
    onSetValue,
    onTextChange,
    placeholder = 'What would you like to know?',
    className,
    mentionOptions,
    mentionGroups,
    mentionLoading = false,
    onMentionClick,
  },
  ref
) {
  const onMentionClickRef = useRef(onMentionClick);
  useEffect(() => {
    onMentionClickRef.current = onMentionClick;
  }, [onMentionClick]);

  const enterSends = useEnterSendsMessage();
  const placeholderRef = useRef(placeholder);
  // The mention list only ever shows a slice of the catalogue, so it needs a
  // way out to everything it did not surface. Offered only where a modal can
  // actually open: this component also renders outside the app shell.
  const openToolsModal = useOpenToolsModal();
  const canOpenModals = useOptionalModalManager() !== null;

  // Annotated explicitly: `handleKeyDown` below references `editor`, so without
  // a type here TS can't resolve `editor`'s type from its own initializer
  // (TS7022/TS7023). This matches the type `useEditor` already inferred.
  const editor: Editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        horizontalRule: false,
      }),
      InstantCodeBlock,
      UiState,
      HorizontalRule,
      Placeholder.configure({ placeholder: () => placeholderRef.current }),
      Color,
      TextStyle,
      Highlight.configure({ multicolor: true }),
      Superscript,
      Subscript,
      Markdown,
      CoteraEmoji,
      ...(mentionOptions !== undefined && mentionGroups !== undefined
        ? [
            CoteraMention.configure({
              onClick: (data: MentionClickData) => {
                onMentionClickRef.current?.(data);
              },
            }),
          ]
        : []),
    ],
    content: value.content?.length === 0 ? undefined : value,
    autofocus: true,
    editorProps: {
      attributes: {
        // `prose-ignore` opts the whole composer out of the global document
        // paragraph rule in styles.css (`.tiptap.ProseMirror p:not(:first-child)`),
        // which otherwise forces 1rem / line-height 1.6 / margin-top 20px onto
        // every paragraph after the first. That rule is meant for the document
        // editor; in the chat box it makes multi-line pastes (e.g. a Slack
        // message) render with a different size/line-height and a big gap than
        // the first line. Opting out lets the chat's own `[&_p]` styles below
        // apply uniformly so every line matches.
        class:
          'tiptap ProseMirror prose-ignore prose prose-sm dark:prose-invert max-w-none min-h-[2.5rem] pb-2 px-0 focus:outline-none w-full [&_p]:text-base [&_p]:my-0.5 [&_p:first]:mt-0 [&_p:last]:mb-0 [&_h1]:my-1 [&_h2]:my-1 [&_h3]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_blockquote]:my-1 [&_pre]:my-1',
      },
      // No `handlePaste` markdown handling: the chat pastes plain text verbatim.
      // The schema can't render tables/most markdown, and a literal markdown
      // table reads fine for the LLM — reinterpreting pasted prose (Slack, etc.)
      // as markdown only caused mismatches. Rich HTML is still normalized below.
      //
      // Normalize external rich HTML pastes (Word, Google Docs, browsers, …) to
      // plain text so source-app formatting does not leak in. Internal Cotera
      // structured content (mentions / @ references / emoji) is detected and
      // left untouched so it keeps parsing as rich nodes.
      transformPastedHTML: (html) => transformExternalPastedHtml(html),
      handleKeyDown: (view, event) => {
        // When a mention/slash/emoji dropdown is visible it owns navigation keys
        // (Enter selects, arrows move, Escape closes).
        const isSuggestionActive = menusActive(view.state);

        if (event.key === 'Enter') {
          // Touch keyboard: Enter inserts a newline; the send button sends.
          // See {@link useEnterSendsMessage} — this is about which keyboard is
          // under the user's hands, not how wide the window is.
          if (!enterSends) {
            return false;
          }
          if (event.isComposing) {
            return false;
          }
          // When a mention/slash/emoji dropdown is visible it owns Enter (it
          // selects an item via useMenuNavigation in the capture phase).
          if (isSuggestionActive) {
            return false;
          }

          // Slack-style structural newline: Shift+Enter continues the current
          // block type instead of sending. Inside a code block it adds an
          // in-block newline (the default Shift+Enter tries a hard break, which
          // a code block rejects, so it must be explicit); inside a list it
          // starts a new item (or lifts out of an empty one); elsewhere it
          // splits into a fresh paragraph. Splitting (rather than a hard break)
          // is what lets `- ` / `1. ` shortcuts start a list on a new line —
          // those input rules only fire at the start of a block. To leave a code
          // block, the down arrow moves past it (handled by the code-block
          // extension's own ArrowDown shortcut).
          if (event.shiftKey) {
            if (editor === null) {
              return false;
            }
            if (editor.isActive('codeBlock')) {
              return editor.chain().focus().newlineInCode().run();
            }
            if (editor.isActive('listItem')) {
              return (
                editor.chain().focus().splitListItem('listItem').run() ||
                editor.chain().focus().liftListItem('listItem').run()
              );
            }
            return editor.chain().focus().splitBlock().run();
          }

          // Plain Enter (and any modifier combo other than Shift) sends.
          event.preventDefault();
          const form = view.dom.closest('form');
          if (form instanceof HTMLFormElement) {
            form.requestSubmit();
          }
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onTextChange?.(ed.getText());
      onChange?.(ed);
    },
  });

  useEffect(() => {
    if (editor !== undefined && editor !== null) {
      onTextChange?.(editor.getText());
    }
  }, [editor, onTextChange]);

  React.useImperativeHandle(
    ref,
    () => ({
      setValue: (text: string) => {
        if (editor !== undefined && editor !== null) {
          editor.commands.setContent(
            text.trim() === ''
              ? { type: 'doc', content: [] }
              : {
                  type: 'doc',
                  content: [
                    {
                      type: 'paragraph',
                      content:
                        text.trim() === '' ? [] : [{ type: 'text', text }],
                    },
                  ],
                }
          );
          onSetValue?.(text);
          onTextChange?.(text);
        }
      },
      getEditor: () => editor ?? null,
      setPlaceholder: (newPlaceholder: string) => {
        if (editor !== undefined && editor !== null) {
          // Update the placeholder extension's options
          placeholderRef.current = newPlaceholder;
          editor?.view.dispatch(editor.view.state.tr); // trigger a re-render
        }
      },
    }),
    [editor, onSetValue, onTextChange]
  );

  if (editor === undefined || editor === null) {
    return (
      <div
        className={cn(
          'flex-1 min-h-[2.5rem] pt-1 pb-2 animate-pulse bg-muted/30 rounded',
          className
        )}
      />
    );
  }

  const showMentions =
    mentionOptions !== undefined && mentionGroups !== undefined;
  return (
    <div
      className={cn('flex-1 min-w-0 min-h-[2.5rem] w-full pt-1', className)}
      data-slot="input-group-control"
    >
      <EditorContext.Provider value={{ editor }}>
        <EditorContent editor={editor} />
        {showMentions && (
          <MentionResourcePicker
            onBrowseAll={canOpenModals ? openToolsModal : undefined}
            options={mentionOptions ?? []}
            groups={mentionGroups}
            isLoading={mentionLoading}
            anchorToEditorStart={true}
            floatingOptions={{ placement: 'top-start' }}
            getOptionId={(option) =>
              option.referenceId !== undefined && option.referenceId !== ''
                ? option.referenceId
                : option.label
            }
          />
        )}
        <ChatActionSlashDropdownMenu />
        <EmojiSuggestionMenu />
      </EditorContext.Provider>
    </div>
  );
});
